import {
  getChatHistory,
  type ChatHistoryParams,
  type ChatHistoryResponse,
} from "@/api/chatHistory";
import type { InfiniteData } from "@tanstack/vue-query";
import { useInfiniteQuery } from "@tanstack/vue-query";
import type { Reactive } from "vue";
import { computed, ref, watch } from "vue";

type ChatHistoryQueryKey = ["chatHistory", Omit<ChatHistoryParams, "page">];

const fetchChatHistoryInView = async ({
  queryKey,
  pageParam = 1,
}: {
  queryKey: ChatHistoryQueryKey;
  pageParam?: number;
}): Promise<ChatHistoryResponse> => {
  const [, filters] = queryKey;
  return getChatHistory({
    ...filters,
    page: pageParam,
  });
};

export function useChatHistoryInfiniteQuery(
  filters: Reactive<Omit<ChatHistoryParams, "page">>,
) {
  const queryKey = ref<ChatHistoryQueryKey>(["chatHistory", { ...filters }]);

  watch(
    () => ({ ...filters }),
    (newFilters) => {
      queryKey.value = ["chatHistory", newFilters];
    },
    { deep: true, immediate: true },
  );

  const queryResult = useInfiniteQuery<
    ChatHistoryResponse,
    Error,
    InfiniteData<ChatHistoryResponse>,
    ChatHistoryQueryKey,
    number
  >({
    queryKey: queryKey,
    queryFn: fetchChatHistoryInView,
    initialPageParam: 1,
    getNextPageParam: (
      lastPage: ChatHistoryResponse,
      allPages: ChatHistoryResponse[],
    ) => {
      const totalItems = lastPage.total_filter;
      const pageSize = filters.size || 10;
      const totalPages = Math.ceil(totalItems / pageSize);
      const currentPage = allPages.length;

      return currentPage < totalPages ? currentPage + 1 : undefined;
    },
    staleTime: 5 * 60 * 1000,
  });

  const chatHistories = computed(() => {
    const pages = queryResult.data.value?.pages;
    if (!pages) return [];
    return pages.flatMap((page: ChatHistoryResponse) => page.data || []);
  });

  const totalFiltered = computed(() => {
    const pages = queryResult.data.value?.pages;
    return pages?.[0]?.total_filter || 0;
  });

  return {
    ...queryResult,
    chatHistories,
    totalFiltered,
  };
}
